import type {
  Warranty,
  WarrantyPrefill,
  WarrantyStats,
  WarrantyStatus,
  WarrantyTemplate,
} from "@shared/modules/x-garanties";
import type { Client, ClientDetail } from "@shared/modules/x-atelier";
import { api } from "../../../core/lib/api";

// Fonctions typées par route de /api/garanties. Les schémas zod vivent dans le
// paquet partagé ; les formes d'ENTRÉE sont redites ici (tout facultatif).

const BASE = "/api/garanties";

export interface WarrantyFields {
  vin?: string | null;
  mileage?: number | null;
  clientName?: string;
  clientPhone?: string | null;
  clientEmail?: string | null;
  clientAddress?: string | null;
  vehicleLabel?: string;
  serviceLabel?: string | null;
  termsMd?: string;
  notes?: string | null;
  /** ISO 8601 ou « AAAA-MM-JJ » (minuit à l'atelier) ; null = brouillon sans date. */
  issuedAt?: string | null;
}

export interface WarrantyCreateInput extends WarrantyFields {
  appointmentId?: string;
  clientId?: string;
  vehicleId?: string;
}

export interface PrefillSource {
  appointmentId?: string;
  clientId?: string;
  vehicleId?: string;
}

export interface WarrantyQuery {
  status?: WarrantyStatus[];
  q?: string;
  clientId?: string;
  limit?: number;
}

function qs(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : "";
}

const json = (method: "POST" | "PATCH", body: unknown): RequestInit => ({
  method,
  body: JSON.stringify(body),
});

export const listWarranties = (query: WarrantyQuery = {}) =>
  api<Warranty[]>(
    `${BASE}${qs({ status: query.status?.join(","), q: query.q, clientId: query.clientId, limit: query.limit })}`,
  );
export const createWarranty = (input: WarrantyCreateInput) =>
  api<Warranty>(BASE, json("POST", input));
export const getWarranty = (id: string) => api<Warranty>(`${BASE}/${id}`);
export const updateWarranty = (id: string, input: WarrantyFields) =>
  api<Warranty>(`${BASE}/${id}`, json("PATCH", input));
/** Coordonnées seules : permis même quand un lien de signature fige le reste du document. */
export const updateWarrantyContact = (
  id: string,
  input: Pick<WarrantyFields, "clientPhone" | "clientEmail">,
) => api<Warranty>(`${BASE}/${id}`, json("PATCH", input));
export const deleteWarranty = (id: string) => api<void>(`${BASE}/${id}`, { method: "DELETE" });
/** Lien de signature ; `renew` refait le lien (l'ancien devient invalide) et le renvoie. */
export const requestSignatureLink = (id: string, renew = false) =>
  api<{
    url: string | null;
    status: string;
    channel: "email" | "sms" | null;
    /** Date du dernier message de signature parti (ISO), s'il y en a un. */
    sentAt?: string | null;
  }>(`${BASE}/${id}/request-signature`, json("POST", renew ? { renew: true } : {}));
export const emitWarranty = (id: string, issuedAt?: string) =>
  api<Warranty>(`${BASE}/${id}/emit`, json("POST", issuedAt ? { issuedAt } : {}));
export const signWarranty = (id: string, signedBy: string) =>
  api<Warranty>(`${BASE}/${id}/sign`, json("POST", { signedBy }));
export const cancelWarranty = (id: string) =>
  api<Warranty>(`${BASE}/${id}/cancel`, json("POST", {}));
export const getPrefill = (source: PrefillSource) =>
  api<WarrantyPrefill>(
    `${BASE}/prefill${qs({ appointmentId: source.appointmentId, clientId: source.clientId, vehicleId: source.vehicleId })}`,
  );
export const getStats = () => api<WarrantyStats>(`${BASE}/stats`);
export const listTemplates = () => api<WarrantyTemplate[]>(`${BASE}/templates`);
export const updateTemplate = (
  id: string,
  input: { name?: string; termsMd?: string; maintenanceMonths?: number },
) => api<WarrantyTemplate>(`${BASE}/templates/${id}`, json("PATCH", input));

/** Adresse absolue du document imprimable (ouvert dans un nouvel onglet). */
export const documentUrl = (id: string) =>
  `${import.meta.env.VITE_API_BASE_URL || ""}${BASE}/${id}/document`;

// ---- Atelier : lecture par HTTP seulement (aucun import du module x-atelier) ----

export interface AtelierClientRow extends Client {
  vehicleCount: number;
}

export const searchAtelierClients = (q: string) =>
  api<AtelierClientRow[]>(`/api/atelier/clients${qs({ q })}`);
export const getAtelierClient = (id: string) => api<ClientDetail>(`/api/atelier/clients/${id}`);
